import { motion, useScroll, useTransform } from "motion/react";
import { useRef } from "react";
import svgPaths from "./svg-paths.js";

function StatCard({ value, label, index }) {
  const cardRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  return (
    <motion.div
      ref={cardRef}
      style={{ y, opacity }}
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: false, margin: "-50px" }}
      transition={{
        duration: 0.6,
        delay: index * 0.15,
        ease: [0.25, 0.4, 0.25, 1],
      }}
      whileHover={{
        scale: 1.03,
        transition: { duration: 0.2 },
      }}
      className="relative bg-[rgba(16,24,40,0.5)] backdrop-blur-sm rounded-[14px] item-border border-[#1e2939] p-6 md:p-8 flex flex-col items-center justify-center gap-2"
    >
      {/* Value */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: false }}
        transition={{
          delay: index * 0.15 + 0.3,
          duration: 0.5,
          type: "spring",
          stiffness: 200,
        }}
      >
        <p className="text-[#fd5b38] text-3xl md:text-4xl lg:text-[36px] font-['Arial'] text-center leading-[40px]">
          {value}
        </p>
      </motion.div>

      {/* Label */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: false }}
        transition={{
          delay: index * 0.15 + 0.5,
          duration: 0.4,
        }}
      >
        <p className="text-[#99a1af] text-sm md:text-[14px] font-['Arial'] text-center leading-[20px]">
          {label}
        </p>
      </motion.div>
    </motion.div>
  );
}

function TestingBadge() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, y: -20 }}
      whileInView={{ opacity: 1, scale: 1, y: 0 }}
      viewport={{ once: false, margin: "-100px" }}
      transition={{
        duration: 0.6,
        ease: [0.25, 0.4, 0.25, 1],
      }}
      whileHover={{
        scale: 1.05,
        transition: { duration: 0.2 },
      }}
      className="inline-flex items-center gap-2 bg-[rgba(253,91,56,0.2)] rounded-full px-6 py-2 mb-6"
    >
      {/* Icon */}
      <motion.div
        className="w-5 h-5"
        whileHover={{ rotate: 360 }}
        transition={{ duration: 0.6 }}
      >
        <svg className="block size-full" fill="none" viewBox="0 0 20 20">
          <g clipPath="url(#clip0_testing)">
            <path
              d={svgPaths.p19086f00}
              stroke="#FD5B38"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.66667"
            />
            <path
              d="M16.667 1.66699V5.00033"
              stroke="#FD5B38"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.66667"
            />
            <path
              d="M18.3333 3.33301H15"
              stroke="#FD5B38"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.66667"
            />
            <path
              d={svgPaths.p7ff4c80}
              stroke="#FD5B38"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.66667"
            />
          </g>
          <defs>
            <clipPath id="clip0_testing">
              <rect fill="white" height="20" width="20" />
            </clipPath>
          </defs>
        </svg>
      </motion.div>

      {/* Text */}
      <span className="text-[#fd5b38] text-sm md:text-[14px] font-['Arial']">
        User Testing Results
      </span>
    </motion.div>
  );
}

export function UserTestingResultsSection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const contentY = useTransform(scrollYProgress, [0, 0.5, 1], [60, 0, -60]);

  const stats = [
    { value: "<5", label: "Minutes to Complete" },
    { value: "95%", label: "Success Rate" },
    { value: "9", label: "Key Screens" },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative py-12 md:py-16 lg:py-24 overflow-hidden"
    >
      {/* Background gradient with parallax */}
      <motion.div
        style={{ y: backgroundY }}
        className="absolute w-64 h-64 bg-[#fd5b38] opacity-5 blur-[100px] rounded-full pointer-events-none top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.05, 0.08, 0.05],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="relative rounded-[16px] item-border border-[rgba(253,91,56,0.3)] p-6 md:p-10 lg:p-12 overflow-hidden"
          style={{
            y: contentY ,
            backgroundImage:
              "linear-gradient(163.498deg, rgba(253, 91, 56, 0.1) 0%, rgba(0, 0, 0, 0) 100%)",
          }}
        >
          {/* Grid pattern overlay */}
          <div
            className="absolute inset-0 opacity-5 pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(253, 91, 56, 0.3) 0.26483%, rgba(0, 0, 0, 0) 0.26483%), linear-gradient(90deg, rgba(253, 91, 56, 0.3) 0%, rgba(0, 0, 0, 0) 0%)",
            }}
          />

          {/* Content */}
          <div className="relative z-10">
            {/* Badge */}
            <div className="text-center mb-6 md:mb-8">
              <TestingBadge />
            </div>

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center mb-8 md:mb-12 lg:mb-16"
            >
              <p className="text-[#d1d5dc] text-base md:text-lg lg:text-[20px] font-['Arial'] leading-[28px] max-w-3xl mx-auto px-4">
                Usability testing confirmed that new users completed all primary
                tasks in under 5 minutes with a success rate of 95%.
              </p>
            </motion.div>

            {/* Stats grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
              {stats.map((stat, index) => (
                <StatCard
                  key={stat.label}
                  value={stat.value}
                  label={stat.label}
                  index={index}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
